import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Public, unauthenticated viewer for a shareable tarot pull.
 * Crawlable — sets document.title + meta tags so iMessage/Twitter/etc.
 * render a real preview when someone pastes the link.
 */
export default function OracleShare() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND}/api/oracle/tarot/share-meta/${token}`)
      .then(({ data }) => {
        setMeta(data);
        document.title = `${data.practitioner}'s tarot pull · Cauldron`;
        upsertMeta("og:title", `${data.practitioner}'s tarot pull`);
        upsertMeta("og:description", data.headline || "Three cards drawn at dawn.");
        upsertMeta("og:image", `${BACKEND}${data.imageUrl}`);
        upsertMeta("og:type", "article");
        upsertMeta("twitter:card", "summary_large_image");
        upsertMeta("twitter:title", `${data.practitioner}'s tarot pull`);
        upsertMeta("twitter:image", `${BACKEND}${data.imageUrl}`);
      })
      .catch(() => setErr("This share card is no longer available."));
  }, [token]);

  if (err) return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-10">
      <div className="font-serif text-2xl italic text-foreground/55" data-testid="share-error">{err}</div>
    </div>
  );

  if (!meta) return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center" data-testid="share-loading">
      <div className="text-foreground/55 italic">Loading…</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="share-page">
      <header className="border-b border-secondary/15 px-6 py-4 flex items-center justify-between">
        <a href="/" className="font-serif text-2xl">cauldron</a>
        <a href="/auth" className="text-xs uppercase tracking-[0.22em] text-secondary border border-secondary/40 px-4 py-2 rounded-sm" data-testid="share-cta">
          Pull your own
        </a>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-12 space-y-6">
        <div>
          <div className="label-mystic">Tarot pull · {meta.day}</div>
          <h1 className="font-serif text-4xl mt-2">{meta.practitioner}</h1>
          {meta.headline && (
            <p className="font-serif italic text-secondary text-2xl mt-3">{meta.headline}</p>
          )}
        </div>

        <div className="parchment p-3" data-testid="share-image-wrap">
          <img src={`${BACKEND}${meta.imageUrl}`}
               alt={`${meta.practitioner}'s tarot pull`}
               className="w-full rounded-sm"
               data-testid="share-image" />
        </div>

        <div className="grid grid-cols-3 gap-3 text-center">
          {(meta.cards || []).map((c) => (
            <div key={c.position} className="border border-secondary/25 p-4">
              <div className="label-mystic text-primary">{c.position}</div>
              <div className="font-serif text-lg mt-1">{c.name}</div>
            </div>
          ))}
        </div>

        <div className="text-center pt-6">
          <a href="/oracle" className="inline-block bg-primary text-primary-foreground px-6 py-3 uppercase tracking-[0.22em] text-xs rounded-sm" data-testid="share-pull-yours">
            Pull your own three cards
          </a>
        </div>
      </main>
    </div>
  );
}

function upsertMeta(prop, content) {
  if (!content) return;
  const sel = prop.startsWith("og:") || prop.startsWith("twitter:") ? `meta[property="${prop}"]` : `meta[name="${prop}"]`;
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement("meta");
    if (prop.startsWith("og:")) el.setAttribute("property", prop);
    else el.setAttribute("name", prop);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}
